// Trang cần cả khung nhìn (sơ đồ mạng lưới cây ngang — isWideShellPath) trên MÁY TÍNH: khung bỏ cả cột menu trái
// lẫn thanh trên; trang tự vẽ MỘT hàng đầu: ☰ (mở menu đè) · tiêu đề · nút của trang · chuông thông báo — như mẫu
// đã duyệt (Main / Agency / Ctv.dc.html). Khung (DashboardLayout) đưa ☰ + chuông xuống qua context này; không có
// context (điện thoại, trang thường) thì WidePageHeader chỉ còn tiêu đề + nút.

import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface WideShell {
  openMenu: () => void
  menuOpen: boolean
  /** Phần cuối hàng đầu do khung cấp (cờ theo dịp, chuông thông báo) — trước đây nằm trên thanh trên. */
  trailing: ReactNode
  /** Trang báo đã vẽ hàng đầu; trả về hàm gỡ. Chưa trang nào báo (đang tải, bị chặn quyền) thì khung tự vẽ hàng ☰ + chuông
      — không thì mất cả menu lẫn đường thoát. Hàm phải ổn định (useCallback) để effect không chạy lại mỗi lần vẽ. */
  registerHeader: () => () => void
}

export const WideShellContext = createContext<WideShell | null>(null)

export function useWideShell(): WideShell | null {
  return useContext(WideShellContext)
}

export function WidePageHeader({ title, actions, className }: { title: ReactNode; actions?: ReactNode; className?: string }) {
  const shell = useWideShell()
  const register = shell?.registerHeader
  useEffect(() => register?.(), [register])
  return (
    <header className={cn('flex flex-wrap items-center gap-3', className)}>
      {shell && (
        <button
          type="button"
          onClick={shell.openMenu}
          aria-label="Mở menu"
          aria-expanded={shell.menuOpen}
          aria-haspopup="dialog"
          className="motion-state grid size-10 shrink-0 place-items-center rounded-[10px] border border-rule bg-raise text-ink hover:bg-paperSoft"
        >
          <Menu className="size-5" aria-hidden />
        </button>
      )}
      <h1 className="min-w-0 flex-[1_1_auto] truncate text-[22px] font-semibold leading-[30px] tracking-normal">{title}</h1>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      {shell?.trailing && <div className="flex shrink-0 items-center gap-2">{shell.trailing}</div>}
    </header>
  )
}

/** Hàng đầu dự phòng do khung vẽ khi trang không có WidePageHeader (đang tải, "chưa được cấp quyền") — chỉ ☰ + chuông. */
export function WideShellFallbackHeader({ shell }: { shell: WideShell }) {
  return (
    <header className="flex items-center gap-3 pb-3.5">
      <button
        type="button"
        onClick={shell.openMenu}
        aria-label="Mở menu"
        aria-expanded={shell.menuOpen}
        aria-haspopup="dialog"
        className="motion-state grid size-10 shrink-0 place-items-center rounded-[10px] border border-rule bg-raise text-ink hover:bg-paperSoft"
      >
        <Menu className="size-5" aria-hidden />
      </button>
      <span className="flex-1" />
      <div className="flex shrink-0 items-center gap-2">{shell.trailing}</div>
    </header>
  )
}
